import { useSession } from 'next-auth/react';
import { useMemo } from 'react';

import { BOT_LANGUAGE_OPTIONS } from '@/data/language-options';
import { TelmaPortalUser } from '@/domain/authorization';

type AllowedPrefixesOptions = NonNullable<
    TelmaPortalUser['allowedPrefixesOptions']
>;
type AllowedMachrConfigurations = NonNullable<
    TelmaPortalUser['allowedMachrConfigurations']
>;

// Module-level so the fallbacks keep a stable identity across renders
const EMPTY_PREFIXES_OPTIONS: AllowedPrefixesOptions = [];
const EMPTY_MACHR_CONFIGURATIONS: AllowedMachrConfigurations = [];

/**
 * Returns the logged-in user's profile data from the session, with list
 * fields defaulted. Permissions live in `useUserPermissions`.
 */
export const useCurrentUser = () => {
    const { data: session } = useSession();
    const user = session?.user;
    const accountId = session?.account;

    return useMemo(
        () => ({
            name: user?.name ?? undefined,
            email: user?.email ?? undefined,
            accountId,
            isEws: !!user?.is_cevro_ews,
            kpiView: user?.kpiView,
            languageOptions: user?.languageOptions ?? BOT_LANGUAGE_OPTIONS,
            voiceOptionsSpecification: user?.voiceOptionsSpecification,
            allowedPrefixesOptions:
                user?.allowedPrefixesOptions ?? EMPTY_PREFIXES_OPTIONS,
            allowedMachrConfigurations:
                user?.allowedMachrConfigurations ?? EMPTY_MACHR_CONFIGURATIONS,
            maxAgeOfAudioDays: user?.maxAgeOfAudioDays,
            maxParallelCalls: user?.maxParallelCalls,
            callerPhoneNumber: user?.callerPhoneNumber,
            sipEndpoint: user?.sipEndpoint,
            sipPrefix: user?.sipPrefix,
            executionWindowStart: user?.executionWindowStart,
            executionWindowEnd: user?.executionWindowEnd,
        }),
        [user, accountId],
    );
};
